import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { C } from "../lib/theme";
import { Card } from "../components/Card";
import { Btn } from "../components/Btn";
import { Eyebrow } from "../components/Eyebrow";
import { Icon } from "../components/Icon";
import { Skeleton } from "../components/Skeleton";
import { ProductCard } from "../components/ProductCard";
import { SizeGuideModal } from "../components/SizeGuideModal";
import { supabase } from "../lib/supabase";
import { useCart } from "../store/cartStore";
import { fmtK } from "../lib/money";
import { gradientForId } from "../lib/gradients";
import { resolveImage } from "../lib/images";
import { timeAgo } from "../lib/dateUtils";
import { colorSwatch, InfoPill } from "./product-detail/helpers";

export function ProductDetail({ productId, onBack, onOpenProduct, onVisitStore }) {
  const add = useCart((s) => s.add);
  const [activeImg, setActiveImg] = useState(0);
  const [variantSel, setVariantSel] = useState({});
  const [qty, setQty] = useState(1);
  const [added, setAdded] = useState(false);
  const [descOpen, setDescOpen] = useState(true);
  const [shipOpen, setShipOpen] = useState(false);
  const [sizeGuideOpen, setSizeGuideOpen] = useState(false);

  // Reset transient UI state whenever the user opens a different product so
  // selections from the previous product don't bleed in.
  useEffect(() => {
    setActiveImg(0);
    setVariantSel({});
    setQty(1);
    setAdded(false);
  }, [productId]);

  // Cached via TanStack Query — staleTime 2 min so a quick back-and-forth
  // between products renders instantly. Mirrors mobile useProductDetail.
  //
  // Important: keep the primary product fetch dead simple — no joins, no
  // optional columns. Joins to `categories` were silently failing in the live
  // DB, returning a generic PostgREST error that we'd interpret as "product
  // not found" and show a blank screen. Enrichment (category, seller, reviews,
  // related) all happens in a parallel Promise.all *after* the product is in
  // hand, and any of them is allowed to come back empty without breaking the
  // page.
  const { data, isLoading: loading, error } = useQuery({
    queryKey: ["product-detail", productId],
    enabled: !!productId,
    staleTime: 2 * 60_000,
    queryFn: async () => {
      const { data: p, error: pErr } = await supabase
        .from("products")
        .select(
          "id, title, description, price, compare_price, stock_qty, low_stock_threshold, images, sizes, attributes, category_id, seller_id, created_at, total_sold, view_count"
        )
        .eq("id", productId)
        .maybeSingle();
      if (pErr) {
        console.error("[ProductDetail] product fetch failed", pErr);
        throw pErr;
      }
      if (!p) return { product: null, seller: null, reviews: [], related: [], category: null };

      const [catRes, sellerRes, itemsRes, relRes] = await Promise.all([
        p.category_id
          ? supabase
              .from("categories")
              .select("name, icon")
              .eq("id", p.category_id)
              .maybeSingle()
          : Promise.resolve({ data: null }),
        p.seller_id
          ? supabase
              .from("sellers")
              .select("id, store_name, store_slug, rating_avg, rating_count, logo_url, delivery_fee, delivery_options, address, product_count")
              .eq("id", p.seller_id)
              .maybeSingle()
          : Promise.resolve({ data: null }),
        supabase
          .from("order_items")
          .select("sub_order_id")
          .eq("product_id", p.id),
        p.category_id
          ? supabase
              .from("products")
              .select("id, title, price, compare_price, images, seller_id, sellers(store_name, address)")
              .eq("category_id", p.category_id)
              .neq("id", p.id)
              .eq("status", "active")
              .is("deleted_at", null)
              .order("created_at", { ascending: false })
              .limit(4)
          : Promise.resolve({ data: [] }),
      ]);

      // Scope reviews to this product: find sub_orders that contain it, then
      // pull reviews tied to those sub_orders. Mirrors mobile useProductDetail.
      const subOrderIds = [
        ...new Set((itemsRes.data || []).map((r) => r.sub_order_id).filter(Boolean)),
      ];
      let reviews = [];
      if (subOrderIds.length > 0) {
        const { data: revData } = await supabase
          .from("reviews")
          .select("id, rating, text, created_at, seller_reply, buyer:users!reviews_buyer_id_fkey(name, avatar_url)")
          .in("sub_order_id", subOrderIds)
          .order("created_at", { ascending: false })
          .limit(10);
        reviews = revData || [];
      }

      return {
        product: { ...p, category: catRes.data || null },
        seller: sellerRes.data || null,
        related: relRes.data || [],
        reviews,
      };
    },
  });

  const product = data?.product || null;
  const seller = data?.seller || null;
  const reviews = data?.reviews || [];
  const related = data?.related || [];

  const images = useMemo(() => (Array.isArray(product?.images) ? product.images : []), [product]);

  // Variants — prefer attributes.variants ([{type, options}, ...]) written by
  // the mobile editor. Fall back to the legacy top-level `sizes` column.
  const variants = useMemo(() => {
    const attrV = product?.attributes?.variants;
    if (Array.isArray(attrV) && attrV.length > 0) {
      return attrV.filter((v) => v && Array.isArray(v.options) && v.options.length > 0);
    }
    const s = product?.sizes;
    if (Array.isArray(s) && s.length > 0) return [{ type: "Size", options: s }];
    return [];
  }, [product]);

  const needsVariants = variants.length > 0;
  const missingVariantTypes = variants
    .map((v) => v.type)
    .filter((t) => !variantSel[t]);
  const stock = product?.stock_qty ?? 0;
  const outOfStock = stock <= 0;
  const low = stock > 0 && stock <= (product?.low_stock_threshold ?? 5);
  const hasDiscount = product?.compare_price && product.compare_price > product.price;
  const discountPct = hasDiscount ? Math.round((1 - product.price / product.compare_price) * 100) : 0;

  // Build a single "size" descriptor from all selected variants so the cart
  // store (keyed by product_id + "|" + size) treats each combination as its
  // own line. Single-variant products keep the plain value (e.g. "L") for
  // back-compat; multi-variant joins with " · " (e.g. "L · Black").
  function buildVariantKey() {
    if (!variants.length) return null;
    const parts = variants.map((v) => variantSel[v.type]).filter(Boolean);
    return parts.length ? parts.join(" · ") : null;
  }

  function onAdd() {
    if (!product) return;
    if (needsVariants && missingVariantTypes.length > 0) return;
    const variantKey = buildVariantKey();
    add({
      product_id: product.id,
      title: product.title,
      price: product.price,
      images: product.images,
      seller_id: product.seller_id,
      seller_name: seller?.store_name,
      stock_qty: stock,
      size: variantKey,
      qty,
    });
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  }

  if (loading) {
    return (
      <div>
        <Btn kind="ghost" size="sm" icon="chevron-left" onClick={onBack}>
          Back
        </Btn>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24, marginTop: 14 }}>
          <Skeleton height={520} />
          <div style={{ display: "grid", gap: 14 }}>
            <Skeleton height={40} />
            <Skeleton height={80} />
            <Skeleton height={200} />
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div>
        <Btn kind="ghost" size="sm" icon="chevron-left" onClick={onBack}>
          Back
        </Btn>
        <div style={{ padding: "60px 0", textAlign: "center", color: C.muted, fontSize: 14 }}>
          {error ? `Couldn't load product: ${error.message || "unknown error"}` : "Product not found."}
        </div>
      </div>
    );
  }

  const mainImg = resolveImage(images[activeImg], "full") || resolveImage(images[activeImg], "med");
  const [g1, g2] = gradientForId(product.id);

  return (
    <div>
      <Btn kind="ghost" size="sm" icon="chevron-left" onClick={onBack}>
        Back
      </Btn>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 28, marginTop: 14, alignItems: "start" }}>
        {/* Gallery — sticky so it stays visible while the info column scrolls */}
        <div style={{ position: "sticky", top: 82, alignSelf: "start" }}>
          <div
            style={{
              width: "100%",
              maxWidth: 520,
              aspectRatio: "1",
              maxHeight: "calc(100vh - 260px)",
              margin: "0 auto",
              borderRadius: 20,
              background: mainImg
                ? `url(${mainImg}) center/cover no-repeat`
                : `linear-gradient(135deg, ${g1}, ${g2})`,
              position: "relative",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              overflow: "hidden",
            }}
          >
            {!mainImg && (
              <span style={{ fontSize: 120, color: C.white, opacity: 0.8 }}>
                {product.category?.icon || "🛍️"}
              </span>
            )}
            {hasDiscount && (
              <div
                style={{
                  position: "absolute",
                  top: 16,
                  left: 16,
                  padding: "5px 10px",
                  borderRadius: 8,
                  background: C.red,
                  color: C.white,
                  fontSize: 12,
                  fontWeight: 800,
                }}
              >
                −{discountPct}%
              </div>
            )}
          </div>

          {images.length > 1 && (
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 8,
                marginTop: 12,
                maxWidth: 520,
                marginLeft: "auto",
                marginRight: "auto",
              }}
            >
              {images.map((entry, i) => {
                const thumb = resolveImage(entry, "thumb") || resolveImage(entry, "med");
                return (
                  <button
                    key={i}
                    onClick={() => setActiveImg(i)}
                    style={{
                      width: 64,
                      height: 64,
                      border: i === activeImg ? `2px solid ${C.hunter}` : "2px solid transparent",
                      borderRadius: 12,
                      padding: 0,
                      cursor: "pointer",
                      background: thumb ? `url(${thumb}) center/cover` : `linear-gradient(135deg, ${g1}, ${g2})`,
                    }}
                  />
                );
              })}
            </div>
          )}
        </div>

        {/* Info */}
        <div>
          {seller && (
            <button
              onClick={() => onVisitStore?.(seller)}
              style={{
                all: "unset",
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                marginBottom: 10,
                padding: "6px 12px",
                borderRadius: 10,
                background: C.cream,
              }}
            >
              <div
                style={{
                  width: 24,
                  height: 24,
                  borderRadius: 8,
                  background: seller.logo_url ? `url(${seller.logo_url}) center/cover` : C.hunter,
                  color: C.dust,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 11,
                  fontWeight: 800,
                }}
              >
                {!seller.logo_url && (seller.store_name?.[0]?.toUpperCase() || "K")}
              </div>
              <span style={{ fontSize: 12, fontWeight: 800, color: C.charcoal }}>{seller.store_name}</span>
              {seller.rating_count > 0 && (
                <span style={{ fontSize: 11, color: C.muted }}>
                  ⭐ {Number(seller.rating_avg).toFixed(1)} · {seller.rating_count}
                </span>
              )}
            </button>
          )}

          <h1 style={{ fontSize: 28, fontWeight: 900, margin: 0, letterSpacing: "-0.6px", color: C.charcoal }}>
            {product.title}
          </h1>

          <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginTop: 10 }}>
            <span style={{ fontSize: 28, fontWeight: 900, color: C.hunter, letterSpacing: "-0.6px" }}>
              {fmtK(product.price)}
            </span>
            {hasDiscount && (
              <span style={{ fontSize: 15, color: C.muted, textDecoration: "line-through" }}>
                {fmtK(product.compare_price)}
              </span>
            )}
          </div>

          <div style={{ display: "flex", gap: 14, marginTop: 10, fontSize: 11.5, color: C.muted }}>
            <span>{product.total_sold || 0} sold</span>
            <span>·</span>
            <span>{product.view_count || 0} views</span>
            {product.category?.name && (
              <>
                <span>·</span>
                <span>{product.category.name}</span>
              </>
            )}
          </div>

          {variants.map((v) => {
            const missing = !variantSel[v.type];
            const isColor = /colou?r/i.test(v.type);
            const isSize = /size/i.test(v.type);
            return (
              <div key={v.type} style={{ marginTop: 18 }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: 8,
                  }}
                >
                  <div
                    style={{
                      fontSize: 10.5,
                      fontWeight: 800,
                      color: C.muted,
                      textTransform: "uppercase",
                      letterSpacing: 1,
                    }}
                  >
                    {v.type}
                    {variantSel[v.type] && (
                      <span style={{ color: C.charcoal, marginLeft: 6 }}>· {variantSel[v.type]}</span>
                    )}
                    {missing && <span style={{ color: C.red }}> · required</span>}
                  </div>
                  {isSize && (
                    <button
                      onClick={() => setSizeGuideOpen(true)}
                      style={{
                        background: "transparent",
                        border: "none",
                        padding: 0,
                        fontSize: 11.5,
                        fontWeight: 700,
                        color: C.hunter,
                        cursor: "pointer",
                        textDecoration: "underline",
                        fontFamily: "inherit",
                      }}
                    >
                      Size guide
                    </button>
                  )}
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {v.options.map((opt) => {
                    const on = variantSel[v.type] === opt;
                    const swatch = isColor ? colorSwatch(opt) : null;
                    return (
                      <button
                        key={opt}
                        onClick={() => setVariantSel((m) => ({ ...m, [v.type]: opt }))}
                        title={opt}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 8,
                          minWidth: isColor ? 0 : 44,
                          height: 44,
                          padding: "0 12px",
                          border: on ? `2px solid ${C.hunter}` : `1px solid ${C.dustDk}`,
                          borderRadius: 10,
                          background: on ? C.cream : C.white,
                          color: C.charcoal,
                          fontSize: 13,
                          fontWeight: 800,
                          cursor: "pointer",
                          fontFamily: "inherit",
                        }}
                      >
                        {swatch && (
                          <span
                            style={{
                              width: 16,
                              height: 16,
                              borderRadius: "50%",
                              background: swatch,
                              border: "1px solid rgba(0,0,0,0.15)",
                              flexShrink: 0,
                            }}
                          />
                        )}
                        {opt}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}

          <div style={{ marginTop: 18, display: "flex", alignItems: "center", gap: 12 }}>
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                border: "1px solid rgba(196,193,183,0.4)",
                borderRadius: 10,
                overflow: "hidden",
              }}
            >
              <button
                onClick={() => setQty((q) => Math.max(1, q - 1))}
                style={{ width: 36, height: 40, border: "none", background: C.white, cursor: "pointer", fontSize: 16 }}
              >
                −
              </button>
              <div style={{ padding: "0 16px", fontSize: 14, fontWeight: 800 }}>{qty}</div>
              <button
                onClick={() => setQty((q) => Math.min(stock || 99, q + 1))}
                style={{ width: 36, height: 40, border: "none", background: C.white, cursor: "pointer", fontSize: 16 }}
              >
                +
              </button>
            </div>
            {low && (
              <span
                style={{
                  fontSize: 11.5,
                  fontWeight: 700,
                  padding: "4px 8px",
                  borderRadius: 6,
                  background: C.amberBg,
                  color: C.amberDk,
                }}
              >
                Only {stock} left
              </span>
            )}
            {outOfStock && (
              <span
                style={{
                  fontSize: 11.5,
                  fontWeight: 700,
                  padding: "4px 8px",
                  borderRadius: 6,
                  background: C.redBg,
                  color: C.red,
                }}
              >
                Out of stock
              </span>
            )}
          </div>

          <div style={{ marginTop: 14, display: "flex", gap: 10 }}>
            <Btn
              full
              size="lg"
              icon={added ? "check" : "shopping-cart"}
              onClick={onAdd}
              disabled={outOfStock || (needsVariants && missingVariantTypes.length > 0)}
            >
              {added ? "Added!" : outOfStock ? "Sold out" : "Add to cart"}
            </Btn>
            <Btn kind="outline" size="lg" icon="heart" />
          </div>

          {/* Description accordion */}
          <Card pad={0} style={{ marginTop: 18 }}>
            <button
              onClick={() => setDescOpen((o) => !o)}
              style={{
                all: "unset",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
                padding: "14px 18px",
                boxSizing: "border-box",
              }}
            >
              <span style={{ fontSize: 13, fontWeight: 800, color: C.charcoal }}>Description & fit</span>
              <Icon name={descOpen ? "chevron-up" : "chevron-down"} size={16} color={C.muted} />
            </button>
            {descOpen && (
              <div style={{ padding: "0 18px 18px", fontSize: 13, color: C.slate, lineHeight: 1.6 }}>
                {product.description || "No description provided."}
              </div>
            )}
          </Card>

          <Card pad={0} style={{ marginTop: 10 }}>
            <button
              onClick={() => setShipOpen((o) => !o)}
              style={{
                all: "unset",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
                padding: "14px 18px",
                boxSizing: "border-box",
              }}
            >
              <span style={{ fontSize: 13, fontWeight: 800, color: C.charcoal }}>Shipping & delivery</span>
              <Icon name={shipOpen ? "chevron-up" : "chevron-down"} size={16} color={C.muted} />
            </button>
            {shipOpen && (
              <div style={{ padding: "0 18px 18px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <InfoPill icon="truck" label="Delivery fee" value={seller?.delivery_fee > 0 ? fmtK(seller.delivery_fee) : "Free"} />
                <InfoPill icon="map-pin" label="From" value={seller?.address || "—"} />
                <InfoPill icon="clock" label="Prep time" value="~15 min" />
                <InfoPill icon="shield" label="Escrow" value="Buyer-protected" />
                {Array.isArray(seller?.delivery_options) && seller.delivery_options.length > 0 && (
                  <div style={{ gridColumn: "1 / -1", display: "flex", flexWrap: "wrap", gap: 6, marginTop: 4 }}>
                    {seller.delivery_options.map((opt) => (
                      <span
                        key={String(opt)}
                        style={{
                          fontSize: 10.5,
                          fontWeight: 700,
                          padding: "3px 8px",
                          borderRadius: 6,
                          background: C.cream,
                          color: C.hunter,
                          textTransform: "capitalize",
                        }}
                      >
                        {String(opt).replace(/_/g, " ")}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}
          </Card>
        </div>
      </div>

      {/* Reviews */}
      <div style={{ marginTop: 32 }}>
        <Eyebrow>Reviews · {seller?.rating_count || 0}</Eyebrow>
        {reviews.length === 0 ? (
          <Card>
            <div style={{ fontSize: 13, color: C.muted, padding: "8px 0" }}>
              No reviews for this seller yet.
            </div>
          </Card>
        ) : (
          <div style={{ display: "grid", gap: 10 }}>
            {reviews.map((r) => (
              <Card key={r.id} pad={16}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                  <div
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: 9,
                      background: r.buyer?.avatar_url ? `url(${r.buyer.avatar_url}) center/cover` : C.hunter,
                      color: C.dust,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 12,
                      fontWeight: 800,
                    }}
                  >
                    {!r.buyer?.avatar_url && (r.buyer?.name?.[0]?.toUpperCase() || "?")}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 12.5, fontWeight: 800 }}>{r.buyer?.name || "Buyer"}</div>
                    <div style={{ fontSize: 10.5, color: C.muted }}>{timeAgo(r.created_at)}</div>
                  </div>
                  <div style={{ fontSize: 12, fontWeight: 800, color: C.hunter }}>
                    {"★".repeat(r.rating)}
                    <span style={{ color: C.dustDk }}>{"★".repeat(5 - r.rating)}</span>
                  </div>
                </div>
                {r.text && <div style={{ fontSize: 13, color: C.slate, lineHeight: 1.5 }}>{r.text}</div>}
                {r.seller_reply && (
                  <div
                    style={{
                      marginTop: 8,
                      padding: 10,
                      background: C.cream,
                      borderRadius: 8,
                      fontSize: 12,
                      color: C.slate,
                    }}
                  >
                    <div style={{ fontWeight: 800, color: C.hunter, marginBottom: 2 }}>Seller reply</div>
                    {r.seller_reply}
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Related */}
      {related.length > 0 && (
        <div style={{ marginTop: 32 }}>
          <Eyebrow>You might also like</Eyebrow>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 14, marginTop: 10 }}>
            {related.map((p) => (
              <ProductCard key={p.id} product={p} onOpen={onOpenProduct} />
            ))}
          </div>
        </div>
      )}

      <SizeGuideModal
        open={sizeGuideOpen}
        onClose={() => setSizeGuideOpen(false)}
        sizes={
          variants.find((v) => /size/i.test(v.type))?.options || []
        }
      />
    </div>
  );
}
